import React from 'react';
import { Link, Outlet, useLocation, useNavigate, useOutletContext, Navigate } from 'react-router-dom';
import { LayoutDashboard, Package, LogOut, Menu, X, Users, Wrench, Camera, Bike, Award, Gift, Sparkles } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';

const AdminLayout = () => {
    const { signOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { userRole } = useOutletContext();
    const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const allNavItems = [
        { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/admin/bikes', label: 'Bicycles', icon: Package },
        { path: '/admin/accessories', label: 'Accessories', icon: Package },
        { path: '/admin/attendance', label: 'Attendance', icon: Users },
        { path: '/admin/maintenance', label: 'Mantenimiento', icon: Wrench },
        { path: '/admin/tuesday-attendance', label: 'Martes de Ruta', icon: Bike },
        { path: '/admin/anniversary', label: 'Aniversario', icon: Award },
        { path: '/admin/aguinaldo', label: 'Aguinaldo Navideño', icon: Gift },
        { path: '/admin/navidad', label: 'Bicis de Navidad', icon: Sparkles },
        { path: '/admin/fotos', label: 'Fotos Actividad', icon: Camera },
    ];

    const navItems = React.useMemo(() => {
        if (userRole === 'staff') {
            return allNavItems.filter(item => 
                item.path === '/admin/attendance' || 
                item.path === '/admin/tuesday-attendance' ||
                item.path === '/admin/anniversary' ||
                item.path === '/admin/aguinaldo' ||
                item.path === '/admin/navidad' ||
                item.path === '/admin/fotos'
            );
        }
        return allNavItems;
    }, [userRole]);

    // Redirect staff away from dashboard
    if (userRole === 'staff' && location.pathname === '/admin') {
        return <Navigate to="/admin/attendance" replace />;
    }


    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar - Desktop */}
            <aside className="hidden md:flex flex-col w-64 bg-black text-white min-h-screen fixed">
                <div className="p-6 border-b border-gray-800">
                    <h1 className="text-xl font-black uppercase tracking-widest">Raymon Admin</h1>
                </div>

                <nav className="flex-1 p-4 space-y-2">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path))
                                ? 'bg-white text-black font-bold'
                                : 'text-gray-400 hover:text-white hover:bg-white/10'
                                }`}
                        >
                            <item.icon size={20} />
                            <span>{item.label}</span>
                        </Link>
                    ))}
                </nav>

                <div className="p-4 border-t border-gray-800">
                    <button
                        onClick={handleSignOut}
                        className="flex items-center gap-3 px-4 py-3 w-full text-red-400 hover:bg-white/5 rounded-lg transition-colors"
                    >
                        <LogOut size={20} />
                        <span>Sign Out</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 md:ml-64 flex flex-col min-w-0">
                {/* Mobile Header */}
                <header className="md:hidden bg-black text-white p-4 flex items-center justify-between">
                    <h1 className="text-lg font-black uppercase tracking-widest">Raymon Admin</h1>
                    <button onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                        {isSidebarOpen ? <X /> : <Menu />}
                    </button>
                </header>

                {/* Mobile Sidebar */}
                {isSidebarOpen && (
                    <div className="fixed inset-0 z-50 md:hidden">
                        <div className="absolute inset-0 bg-black/50" onClick={() => setIsSidebarOpen(false)} />
                        <div className="absolute inset-y-0 left-0 w-64 bg-black text-white p-4 flex flex-col">
                            <nav className="flex-1 space-y-2 mt-8">
                                {navItems.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setIsSidebarOpen(false)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === item.path
                                            ? 'bg-white text-black font-bold'
                                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                                            }`}
                                    >
                                        <item.icon size={20} />
                                        <span>{item.label}</span>
                                    </Link>
                                ))}
                            </nav>
                            <button
                                onClick={handleSignOut}
                                className="flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-white/5 rounded-lg"
                            >
                                <LogOut size={20} />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Page Content */}
                <main className="p-6 md:p-8 overflow-y-auto h-full">
                    <Outlet context={{ userRole }} />
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
